'use client';

import Link from 'next/link';
import PropTypes from 'prop-types';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import PinterestIcon from '@mui/icons-material/Pinterest';
import XIcon from '@mui/icons-material/X';
import { Box, Container, Grid, IconButton, Stack, Typography } from '@mui/material';
import WebsiteButton from '@/components/website/WebsiteButton';
import { HOME_FOOTER, HOME_FOOTER_SOCIAL_LINKS } from '@/components/home-landing/home-landing-data';
import { STUDIO_PATH } from '@/consts/routes';
import FooterNewsletterSignup from './FooterNewsletterSignup';
import WebsiteFooterLegalLinks from './WebsiteFooterLegalLinks';
import { WEBSITE_PAGE_BACKGROUND, WEBSITE_PAGE_BACKGROUND_MUTED } from '@/consts/website-theme';
import {
  WEBSITE_FOOTER_BOOK_CTA,
  WEBSITE_FOOTER_COMPANY_LINKS,
  WEBSITE_FOOTER_PORTFOLIO_LINKS,
  WEBSITE_STUDIO_CONTACT,
} from './website-footer-data';

const SOCIAL_ICONS = {
  facebook: FacebookIcon,
  instagram: InstagramIcon,
  linkedin: LinkedInIcon,
  x: XIcon,
  pinterest: PinterestIcon,
};

const columnTitleSx = {
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.14em',
  textTransform: 'uppercase',
  color: 'text.primary',
  mb: 2,
};

const footerLinkSx = {
  display: 'block',
  color: 'text.secondary',
  textDecoration: 'none',
  fontSize: 14,
  lineHeight: 1.6,
  py: 0.35,
  transition: 'color 0.2s ease',
  '&:hover': {
    color: 'text.primary',
    textDecoration: 'underline',
  },
};

const bottomFooterTextSx = {
  color: 'rgba(0, 0, 0, 0.55)',
  letterSpacing: '0.1em',
  fontSize: 10,
  textTransform: 'uppercase',
};

const bottomFooterLinkSx = {
  color: 'rgba(0, 0, 0, 0.65)',
  textDecoration: 'none',
  letterSpacing: '0.12em',
  fontSize: 10,
  textTransform: 'uppercase',
  '&:hover': {
    color: 'text.primary',
    textDecoration: 'underline',
  },
};

const bottomSocialIconButtonSx = {
  color: 'rgba(0, 0, 0, 0.5)',
  '&:hover': {
    color: 'text.primary',
    bgcolor: 'transparent',
  },
};

function FooterLinkColumn({ title, links }) {
  return (
    <Box>
      <Typography component="h2" sx={columnTitleSx}>
        {title}
      </Typography>
      <Stack component="nav" spacing={0.25} aria-label={title}>
        {links.map(({ label, href }) => (
          <Box key={label} component={Link} href={href} sx={footerLinkSx}>
            {label}
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

FooterLinkColumn.propTypes = {
  title: PropTypes.string.isRequired,
  links: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      href: PropTypes.string.isRequired,
    }),
  ).isRequired,
};

export default function WebsiteFooter() {
  const currentYear = new Date().getFullYear();
  const { establishedYear, companyName, webCredit } = HOME_FOOTER;
  const { addressLines, email, phone, phoneHref } = WEBSITE_STUDIO_CONTACT;

  return (
    <Box
      component="footer"
      sx={{
        flexShrink: 0,
        mt: 'auto',
        bgcolor: WEBSITE_PAGE_BACKGROUND,
        color: '#0a0a0a',
        borderTop: '1px solid',
        borderColor: 'rgba(0, 0, 0, 0.08)',
      }}
    >
      <Box sx={{ bgcolor: WEBSITE_PAGE_BACKGROUND_MUTED, py: { xs: 5, md: 7 } }}>
        <Container maxWidth="xl">
          <Grid container spacing={{ xs: 4, md: 5 }}>
            <Grid item xs={6} md={3}>
              <FooterLinkColumn title="Explore" links={WEBSITE_FOOTER_COMPANY_LINKS} />
            </Grid>
            <Grid item xs={6} md={3}>
              <FooterLinkColumn title="Portfolio" links={WEBSITE_FOOTER_PORTFOLIO_LINKS} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box
                component={Link}
                href={STUDIO_PATH}
                sx={{
                  ...columnTitleSx,
                  display: 'inline-block',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                Studio
              </Box>
              <Stack spacing={1.25}>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                  {addressLines.map(line => (
                    <Box key={line} component="span" sx={{ display: 'block' }}>
                      {line}
                    </Box>
                  ))}
                </Typography>
                <Box component="a" href={`mailto:${email}`} sx={footerLinkSx}>
                  {email}
                </Box>
                <Box component="a" href={phoneHref} sx={footerLinkSx}>
                  {phone}
                </Box>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Stack spacing={2.5} sx={{ width: '100%' }}>
                <Box>
                  <Typography component="h2" sx={columnTitleSx}>
                    {WEBSITE_FOOTER_BOOK_CTA.title}
                  </Typography>
                  <WebsiteButton href={WEBSITE_FOOTER_BOOK_CTA.href} fullWidth>
                    {WEBSITE_FOOTER_BOOK_CTA.label}
                  </WebsiteButton>
                </Box>
                <FooterNewsletterSignup />
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box sx={{ py: { xs: 2.5, md: 3 }, px: 2 }}>
        <Stack spacing={1.5} alignItems="center">
          <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
            {HOME_FOOTER_SOCIAL_LINKS.map(({ label, href, icon }) => {
              const Icon = SOCIAL_ICONS[icon];
              return (
                <IconButton
                  key={icon}
                  component="a"
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  size="small"
                  sx={bottomSocialIconButtonSx}
                >
                  <Icon fontSize="small" />
                </IconButton>
              );
            })}
          </Stack>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 0.75, sm: 1.5 }}
            alignItems="center"
            justifyContent="center"
          >
            <Typography component="p" sx={bottomFooterTextSx}>
              © {establishedYear}–{currentYear} {companyName}
            </Typography>
            <Typography
              component="span"
              aria-hidden
              sx={{
                ...bottomFooterTextSx,
                display: { xs: 'none', sm: 'inline' },
                opacity: 0.5,
              }}
            >
              ·
            </Typography>
            <Box
              component={Link}
              href={webCredit.href}
              target="_blank"
              rel="noopener noreferrer"
              sx={bottomFooterLinkSx}
            >
              {webCredit.label}
            </Box>
          </Stack>

          <WebsiteFooterLegalLinks
            bottomFooterLinkSx={bottomFooterLinkSx}
            bottomFooterTextSx={bottomFooterTextSx}
          />
        </Stack>
      </Box>
    </Box>
  );
}
